import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Card, Field, FieldRow, Input, Section, Select } from "@/components/kit";
import { type MasterResult, createRoleAction, updateRoleAction } from "@/lib/actions/roles";
import type { RoleDetail } from "@/lib/api/types";

/**
 * A role — jockey, umpire, captain.
 *
 * This file used to import `FixtureEntryForm.module.css`: a role editor wearing
 * a fixture form's clothes, because that stylesheet happened to have a `.field`
 * class in it. It was the single clearest symptom in the problem statement, and
 * the reason the shared parts had to land before any screen was replaced. Now
 * it is assembled from them and owns no CSS at all.
 */

const CATEGORIES = [
  { value: "COMPOSITION", label: "Someone a side fields — captain, keeper" },
  { value: "CONNECTION", label: "Someone attached to an entrant — jockey, coach" },
  { value: "OFFICIAL", label: "Someone attached to the fixture — umpire, judge" },
];

export function RoleForm({ sports, initial }: { sports: string[]; initial?: RoleDetail }) {
  const editing = initial !== undefined;
  const [sportCode, setSportCode] = useState(initial?.sportCode ?? "");
  const [code, setCode] = useState(initial?.code ?? "");
  const [category, setCategory] = useState(initial?.category ?? "CONNECTION");
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [result, setResult] = useState<MasterResult>(undefined);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setResult(undefined);
    const answer = editing
      ? await updateRoleAction(initial.id, {
          name,
          category,
          description: description || undefined,
        })
      : await createRoleAction({
          code,
          category,
          name,
          sportCode: sportCode || undefined,
          description: description || undefined,
        });
    if (answer) {
      setResult(answer);
      setBusy(false);
    } else {
      // Saved. The action used to redirect from inside Next; the form owns the
      // navigation now.
      navigate("/roles");
    }
  }

  return (
    <form onSubmit={submit}>
      {result && (
        <Card>
          <p style={{ margin: 0, color: "var(--bad)", fontSize: "var(--t-sm)" }} role="alert">
            {result.error}
          </p>
        </Card>
      )}

      <Section
        title={editing ? `Edit ${initial.code}` : "A new role"}
        note="A role is how someone is attached to a fixture. The code never changes once it is in use."
      >
        <div style={{ display: "grid", gap: "var(--s4)" }}>
          <FieldRow>
            <Field
              label="Sport"
              hint="Leave empty for a role every sport can use"
              htmlFor="r-sport"
            >
              <Select
                id="r-sport"
                value={sportCode ?? ""}
                disabled={editing}
                onChange={(e) => setSportCode(e.target.value)}
              >
                <option value="">Every sport</option>
                {sports.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Code" required hint="jockey, umpire, captain" htmlFor="r-code">
              <Input
                id="r-code"
                value={code}
                required
                disabled={editing}
                placeholder="jockey"
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
          </FieldRow>

          <Field label="What kind of role" htmlFor="r-category">
            <Select
              id="r-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>

          <FieldRow>
            <Field label="Name" required htmlFor="r-name">
              <Input
                id="r-name"
                value={name}
                required
                placeholder="Jockey"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Description" htmlFor="r-description">
              <Input
                id="r-description"
                value={description ?? ""}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          </FieldRow>

          <div>
            <Button tone="primary" type="submit" disabled={busy}>
              {busy ? "Saving…" : editing ? "Save changes" : "Create role"}
            </Button>
          </div>
        </div>
      </Section>
    </form>
  );
}
